/**
 * The wait (v1 ticket 41): while a new moment's analysis runs, the learner sees their own moment, not a
 * spinner. The analysis answers whole, so nothing here claims progress it cannot see.
 *
 * - A recording: "Listening back to what you caught…" over its waveform, drawn on this device from the clip
 *   already in memory (`decodeAudioData`; nothing is uploaded again), with a light moving slowly along it.
 *   A clip that cannot be decoded keeps the plain line and a play button. It plays aloud only on a tap,
 *   from the device's copy, and the light then follows what plays.
 * - A typed note: inked line by line under "Turning your note into Catalan…" (the pair's target).
 * - Under either, the shape of what is coming, one turn at a time: under a recording, skeleton transcript lines as
 *   two voices taking turns; under a note (once inked), a small skeleton scene, the other person and "You" in turn,
 *   the learner's bubble quoting the note's start. A stepped caption names what is being made, never how far along.
 * - After 20 s, one more line: busy recordings can take half a minute.
 * - Reduced motion: the waveform, the note and the turns stand still, all shown at once.
 *
 * Light DOM, styled inline like the record screen. The arithmetic is in `wait-lines.ts`.
 */
import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { currentTarget, t, tOr } from '../i18n';
import {
  INK_MS, inkDelays, inkEnd, inkFromRight, LONG_WAIT_MS, noteLines, noteQuote, peaksOf, SCENE_TURNS, TRANSCRIPT_TURNS,
  turnDelays, WAVE_BARS,
} from './wait-lines';
import { skeletonLine } from './wait-bits';

/** When the transcript's first skeleton line comes in under the waveform. */
const TRANSCRIPT_START_MS = 900;

/** What is being analysed: the clip in memory, or the note as typed. */
export type WaitCapture = { kind: 'audio'; blob: Blob; shared?: boolean } | { kind: 'text'; text: string };

/** One slow sweep of the light along the waveform. */
const SWEEP_MS = 6500;
/** The waveform's height in CSS pixels. */
const WAVE_HEIGHT = 64;
/** A decode that has not answered by then counts as undecodable. */
const DECODE_TIMEOUT_MS = 5000;

/**
 * The clip's waveform, decoded on this device, or null when it cannot be (an unknown format, no Web Audio,
 * an empty clip, or no answer in time).
 */
export async function decodePeaks(blob: Blob, bars = WAVE_BARS): Promise<number[] | null> {
  try {
    const w = window as typeof window & { webkitOfflineAudioContext?: typeof OfflineAudioContext };
    const Ctx = w.OfflineAudioContext ?? w.webkitOfflineAudioContext;
    if (!Ctx) return null;
    const data = await blob.arrayBuffer();
    const ctx = new Ctx(1, 1, 44100);
    const decoded = new Promise<AudioBuffer>((resolve, reject) => {
      // Older Safari only answers through the callbacks; the rest also return a promise.
      const p = ctx.decodeAudioData(data, resolve, reject) as Promise<AudioBuffer> | undefined;
      if (p && typeof p.then === 'function') p.then(resolve, reject);
    });
    const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), DECODE_TIMEOUT_MS));
    const buf = await Promise.race([decoded, timeout]);
    if (!buf || !buf.length) return null;
    const channels = Array.from({ length: buf.numberOfChannels }, (_, i) => buf.getChannelData(i));
    const peaks = peaksOf(channels, bars);
    return peaks.length ? peaks : null;
  } catch {
    return null;
  }
}

@customElement('moment-wait')
export class MomentWait extends LitElement {
  @property({ attribute: false }) capture?: WaitCapture;
  /** The waveform: undefined while decoding, null when the clip cannot be drawn. */
  @state() private peaks?: number[] | null;
  /** How many of the note's lines are inked. */
  @state() private inked = 0;
  /** How many of the pre-drawn turns (the scene's bubbles, the transcript's lines) are in. */
  @state() private turns = 0;
  @state() private long = false;
  @state() private playing = false;
  private lines: string[] = [];
  private still = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  private timers: number[] = [];
  private raf = 0;
  private audio?: HTMLAudioElement;
  private url = '';
  private colours = { dim: '#9aa3b2', accent: '#ff7a59' };
  /**
   * Bumped on every new capture and on every teardown (`end`), so a decode that answers late, for an older
   * capture or after the element left the page, is dropped.
   */
  private run = 0;

  protected createRenderRoot() { return this; }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.end();
  }

  connectedCallback() {
    super.connectedCallback();
    // Reattached with the same capture (moved in the page): start again.
    if (this.capture && this.run > 0 && this.timers.length === 0) this.begin();
  }

  protected willUpdate(changed: Map<string, unknown>) {
    if (changed.has('capture')) this.begin();
  }

  protected updated() {
    if (this.capture?.kind === 'audio') this.kick();
  }

  private begin() {
    this.end();
    const run = ++this.run;
    this.peaks = undefined;
    this.long = false;
    this.playing = false;
    this.turns = 0;
    const c = this.capture;
    if (!c) return;
    this.timers.push(window.setTimeout(() => (this.long = true), LONG_WAIT_MS));
    if (c.kind === 'text') {
      this.lines = noteLines(c.text);
      if (this.still) {
        this.inked = this.lines.length;
      } else {
        this.inked = 0;
        // One frame first, so each line's ink runs as a transition from nothing.
        inkDelays(this.lines.length).forEach((ms, i) => this.timers.push(window.setTimeout(() => (this.inked = Math.max(this.inked, i + 1)), ms + 60)));
      }
      this.pace(SCENE_TURNS.length, this.still ? 0 : inkEnd(this.lines.length) + 60);
      return;
    }
    this.pace(TRANSCRIPT_TURNS.length, TRANSCRIPT_START_MS);
    const css = getComputedStyle(document.documentElement);
    this.colours = {
      dim: css.getPropertyValue('--text-dim').trim() || this.colours.dim,
      accent: css.getPropertyValue('--accent').trim() || this.colours.accent,
    };
    void decodePeaks(c.blob).then((peaks) => {
      if (run === this.run && this.isConnected) this.peaks = peaks;
    });
  }

  /** Brings the `n` pre-drawn turns in one at a time from `start`; all at once under reduced motion. */
  private pace(n: number, start: number) {
    if (this.still) {
      this.turns = n;
      return;
    }
    turnDelays(n, start).forEach((ms, i) => this.timers.push(window.setTimeout(() => (this.turns = Math.max(this.turns, i + 1)), ms)));
  }

  private end() {
    // Any decode still in flight now belongs to a torn-down run.
    this.run++;
    for (const id of this.timers) clearTimeout(id);
    this.timers = [];
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    if (this.audio) {
      this.audio.pause();
      this.audio.onended = this.audio.onpause = this.audio.onplay = null;
      // Let go of the clip before its URL goes, so the element never asks for it again.
      this.audio.removeAttribute('src');
      this.audio.load();
      this.audio = undefined;
    }
    if (this.url) URL.revokeObjectURL(this.url);
    this.url = '';
    this.playing = false;
  }

  /** A tap plays the device's copy of the clip, or pauses it; nothing plays by itself. */
  private toggle = () => {
    const c = this.capture;
    if (c?.kind !== 'audio') return;
    if (!this.audio) {
      this.url = URL.createObjectURL(c.blob);
      const a = new Audio(this.url);
      a.onplay = () => { this.playing = true; this.kick(); };
      a.onpause = () => { this.playing = false; this.kick(); };
      a.onended = () => { this.playing = false; this.kick(); };
      this.audio = a;
    }
    if (this.audio.paused) {
      this.audio.play().catch(() => { this.playing = false; });
    } else {
      this.audio.pause();
    }
  };

  /** One more frame (the still waveform redraws only when something changed); none once the element left. */
  private kick() {
    if (!this.raf && this.peaks && this.isConnected) this.raf = requestAnimationFrame(this.draw);
  }

  private draw = (now: number) => {
    this.raf = 0;
    if (!this.isConnected) return;
    const canvas = this.querySelector<HTMLCanvasElement>('canvas.wait-canvas');
    const peaks = this.peaks;
    if (!canvas || !peaks) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = Math.max(1, Math.round(canvas.clientWidth * dpr));
    const h = Math.round(WAVE_HEIGHT * dpr);
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
    const g = canvas.getContext('2d');
    if (!g) return;
    g.clearRect(0, 0, w, h);
    const a = this.audio;
    const played = a && this.playing && a.duration > 0 && Number.isFinite(a.duration) ? a.currentTime / a.duration : -1;
    // The light: where the clip is playing, else a slow sweep; none when motion is reduced.
    const light = played >= 0 ? (this.still ? -1 : played) : this.still ? -1 : (now % SWEEP_MS) / SWEEP_MS;
    const n = peaks.length;
    const slot = w / n;
    const barW = Math.max(1, slot * 0.55);
    const radius = 0.1;
    for (let i = 0; i < n; i++) {
      const at = (i + 0.5) / n;
      const bh = Math.max(2 * dpr, peaks[i] * (h - 4 * dpr));
      const x = at * w - barW / 2;
      const y = (h - bh) / 2;
      const heard = played >= 0 && at <= played;
      g.globalAlpha = heard ? 0.85 : 0.45;
      g.fillStyle = heard ? this.colours.accent : this.colours.dim;
      this.bar(g, x, y, barW, bh);
      const glow = light < 0 ? 0 : Math.max(0, 1 - Math.abs(at - light) / radius);
      if (glow > 0) {
        g.globalAlpha = glow * 0.9;
        g.fillStyle = this.colours.accent;
        this.bar(g, x, y, barW, bh);
      }
    }
    g.globalAlpha = 1;
    if ((!this.still || this.playing) && this.isConnected) this.raf = requestAnimationFrame(this.draw);
  };

  private bar(g: CanvasRenderingContext2D, x: number, y: number, bw: number, bh: number) {
    if (typeof g.roundRect === 'function') {
      g.beginPath();
      g.roundRect(x, y, bw, bh, Math.min(bw / 2, bh / 2));
      g.fill();
    } else {
      g.fillRect(x, y, bw, bh);
    }
  }

  render() {
    const c = this.capture;
    if (!c) return nothing;
    const audio = c.kind === 'audio';
    const head = audio
      ? t(c.shared ? 'wait.shared' : 'wait.listening')
      : t('wait.imagining', { language: tOr(`lang.${currentTarget()}`, currentTarget()) });
    return html`
      <div class="card moment-wait" data-kind=${c.kind} style="padding:18px 16px">
        <div class="wait-head" role="status" style="font-size:17px;line-height:1.4;margin-bottom:14px">${head}</div>
        ${audio ? this.renderWave() : this.renderNote()}
        ${audio ? this.renderTranscript() : this.renderTalk(c.text)}
        <wait-caption .lines=${audio
          ? [t('wait.rec.speakers'), t('wait.rec.writing'), t('wait.rec.words')]
          : [t('wait.scene.who'), t('wait.scene.lines'), t('wait.scene.turn'), t('wait.scene.translate')]}></wait-caption>
        ${this.long
          ? html`<div class="muted small wait-long" role="status" style="margin-top:14px">${t(audio ? 'wait.long' : 'wait.longNote')}</div>`
          : nothing}
      </div>
    `;
  }

  private renderWave() {
    const label = this.playing ? t('wait.pause') : t('wait.play');
    if (this.peaks === null) {
      // Not drawable here: the plain line above stands alone, and the clip still plays on a tap.
      return html`<button type="button" class="btn ghost wait-play" aria-pressed=${this.playing ? 'true' : 'false'} @click=${this.toggle}>
        <span aria-hidden="true" dir="ltr">${this.playing ? '❚❚' : '▶'}</span> ${label}
      </button>`;
    }
    return html`
      <button type="button" class="wait-wave" dir="ltr" aria-label=${label} aria-pressed=${this.playing ? 'true' : 'false'}
        data-drawn=${this.peaks ? 'true' : 'false'} @click=${this.toggle}
        style="display:flex;align-items:center;gap:10px;width:100%;padding:8px 10px;border:1px solid var(--line);border-radius:var(--radius-s);background:var(--bg-elev-2);color:var(--text);cursor:pointer;font:inherit">
        <span aria-hidden="true" style="flex:none;width:32px;height:32px;border-radius:50%;display:grid;place-items:center;background:var(--accent-soft);color:var(--accent);font-size:13px">
          ${this.playing ? '❚❚' : '▶'}
        </span>
        <canvas class="wait-canvas" aria-hidden="true" style="flex:1;min-width:0;width:100%;height:${WAVE_HEIGHT}px;display:block"></canvas>
      </button>
    `;
  }

  private renderTranscript() {
    return transcriptTurns(this.turns);
  }

  private renderTalk(text: string) {
    return sceneTurns(this.turns, noteQuote(text));
  }

  private renderNote() {
    const ink = this.still ? '' : `transition:clip-path ${INK_MS}ms ease-out, opacity ${INK_MS}ms ease-out;`;
    return html`
      <div class="wait-note" style="font-size:18px;line-height:1.55">
        ${this.lines.map((line, i) => {
          const shown = i < this.inked;
          const hidden = inkFromRight(line) ? 'inset(0 0 0 100%)' : 'inset(0 100% 0 0)';
          return html`<div class="wait-line" dir="auto" data-inked=${shown ? 'true' : 'false'}
            style="${ink}clip-path:${shown ? 'inset(0 0 0 0)' : hidden};opacity:${shown ? 1 : 0}">${line}</div>`;
        })}
      </div>
    `;
  }
}

/** A neutral head and shoulders: the other person, not yet imagined. */
const SILHOUETTE = html`<svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true"><circle cx="10" cy="7" r="3.6" fill="currentColor"/><path d="M3.2 18c.9-3.8 3.6-5.8 6.8-5.8s5.9 2 6.8 5.8z" fill="currentColor"/></svg>`;

/** The transcript to come: skeleton lines as two voices (A, B) taking turns, each from its own side. */
function transcriptTurns(shown: number) {
  return html`<div class="wait-talk wait-transcript" aria-hidden="true">
    ${TRANSCRIPT_TURNS.slice(0, shown).map((who, i) => html`<div class="wait-turn ${who}" data-turn=${i}>
      <span class="wait-who">${who.toUpperCase()}</span>
      <div class="wait-bubble">${skeletonLine(i % 2 ? 0.8 : 0.95)}${i % 3 === 1 ? nothing : skeletonLine(i % 2 ? 0.45 : 0.6)}</div>
    </div>`)}
  </div>`;
}

/** The scene to come: skeleton bubbles, the other person and "You" in turn, the first "You" quoting the note's start. */
function sceneTurns(shown: number, quote: string) {
  let quoted = false;
  return html`<div class="wait-talk wait-scene" aria-hidden="true">
    ${SCENE_TURNS.slice(0, shown).map((who, i) => {
      const q = who === 'you' && !quoted && quote ? ((quoted = true), quote) : '';
      return html`<div class="wait-turn ${who}" data-turn=${i}>
        ${who === 'other' ? html`<span class="wait-who">${SILHOUETTE}</span>` : nothing}
        <div class="wait-bubble">
          ${who === 'you' ? html`<span class="wait-you">${t('moment.speakerYou')}</span>` : nothing}
          ${q ? html`<span class="wait-quote" dir="auto">“${q}”</span>` : skeletonLine(i % 2 ? 0.7 : 0.9)}
          ${skeletonLine(q ? 0.55 : 0.5)}
        </div>
      </div>`;
    })}
  </div>`;
}

declare global {
  interface HTMLElementTagNameMap {
    'moment-wait': MomentWait;
  }
}
